import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProfile } from '../lib/api-client';
import { useAuth } from '../context/AuthContext';
import ProfileClient from '../components/desktop/ProfileClient';
import LoadingScreen from '../components/desktop/LoadingScreen';
import { profileEventBus } from '../lib/profileEventBus';

import { fetchWithAuth } from '../lib/fetchWithAuth';
export default function UserProfile() {
  const { id, username } = useParams<{ id?: string; username?: string }>();
  const navigate = useNavigate();
  const { user: authUser, loading: authLoading, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [profileData, setProfileData] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Reset state when route parameters change
  useEffect(() => {
    setLoading(true);
    setProfileData(null);
    setCurrentUser(null);
  }, [id, username]);

  useEffect(() => {
    async function loadData() {
      const identifier = username || id;
      if (!identifier) {
        navigate('/');
        return;
      }

      // Wait for auth to finish loading before proceeding
      if (authLoading) {
        return;
      }

      // Reset state before loading new profile
      setLoading(true);
      setProfileData(null);
      setCurrentUser(null);

      try {
        // Get admin status from AuthContext user
        if (authUser) {
          setIsAdmin(authUser.isSiteAdmin || false);
          setIsSuperAdmin(authUser.isSuperAdmin || false);
        }

        // If username is provided, fetch by username via direct API call; otherwise use getProfile
        let profile;
        if (username) {
          // Direct API call for username-based lookup
          const response = await fetchWithAuth(`/api/profile/username/${username}`, {
            credentials: 'include'
          });
          if (!response.ok) throw new Error('Profile not found');
          profile = await response.json();
        } else {
          profile = await getProfile(id!);
        }

        if (authUser) {
          // IDs are Company ULID strings (not numeric) — compare as strings.
          const currentUserId = String(authUser.id);
          const profileUserId = String(profile.user.id);

          setCurrentUser({
            id: authUser.id.toString(),
            username: authUser.username,
            displayName: authUser.name || authUser.username,
            avatar: authUser.profilePicture,
            bio: authUser.profileBio,
            isCurrentUser: currentUserId === profileUserId,
            isSiteAdmin: authUser.isSiteAdmin || false,
            isSiteModerator: authUser.isSiteModerator || false,
            isSuperAdmin: authUser.isSuperAdmin || false,
            posts: [],
          });

          setProfileData({
            ...profile.user,
            title: profile.user.profileTitle, // Map profileTitle from API to title for component
            isCurrentUser: currentUserId === profileUserId,
          });
        } else {
          setProfileData({
            ...profile.user,
            title: profile.user.profileTitle, // Map profileTitle from API to title for component
            isCurrentUser: false,
          });
        }

        setLoading(false);
      } catch (error) {
        // Don't redirect, just show error state
        setLoading(false);
      }
    }

    loadData();
  }, [id, username, navigate, authUser, authLoading, refreshKey]);

  const handleFollowChange = async () => {
    // Refresh auth user data (for navbar profile pic, etc)
    await refreshUser();
    // Trigger a refetch by updating the refresh key
    setRefreshKey(prev => prev + 1);
  };

  // Listen for profile updates and refresh auth user
  useEffect(() => {
    const unsubscribe = profileEventBus.on('profile:updated', async (data) => {
      // Refresh auth user to update navbar profile picture
      await refreshUser();
      // If it's the current profile being viewed, also trigger a refetch
      if (authUser && data.userId === authUser.id.toString()) {
        setRefreshKey(prev => prev + 1);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [refreshUser, authUser]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!profileData) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--app-bg)', color: 'var(--text-color)' }}>
        <div className="text-xl">Profile not found</div>
      </div>
    );
  }

  return (
    <ProfileClient
      user={profileData}
      currentUser={currentUser}
      isAdmin={isAdmin}
      isSuperAdmin={isSuperAdmin}
      onFollowChange={handleFollowChange}
    />
  );
}
